import { useEffect, useState } from "react";
import { Loader2, UserPlus, Trash2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { teamList, teamCreate, teamDelete } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "tenant_member" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try { const r = await teamList(); setUsers(r.users); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await teamCreate({
        ...form,
        customer_id: user.customer_id,
        status: "active",
      });
      toast.success("Team member created");
      setAddOpen(false);
      setForm({ name: "", email: "", password: "", role: "tenant_member" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try { await teamDelete(id); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">
              {user?.customer?.name || "Team"}
            </div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
              Team
            </h1>
            <p className="text-zinc-600 mt-2 text-sm">
              Invite teachers, admins, or communications staff to create components alongside you.
            </p>
          </div>
          <Button onClick={() => setAddOpen(true)}
                  data-testid="team-add-btn"
                  className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
            <UserPlus className="h-4 w-4 mr-2" /> Add team member
          </Button>
        </div>

        <div className="border border-zinc-200 bg-white rounded-md">
          {loading ? (
            <div className="h-40 grid place-items-center text-zinc-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">No team members yet.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {users.map((u) => (
                <li key={u.id} className="px-5 py-4 flex items-center gap-3" data-testid={`team-user-${u.id}`}>
                  <div className="h-9 w-9 rounded-full bg-zinc-100 grid place-items-center text-sm font-semibold text-zinc-600">
                    {(u.name || u.email)[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-950 truncate">{u.name || u.email}</div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{u.role.replace("_", " ")}</Badge>
                  {u.id !== user.id ? (
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => onDelete(u.id)}
                      className="text-zinc-400 hover:text-red-600"
                      data-testid={`team-delete-${u.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : <span className="text-[10px] text-zinc-400 px-2">you</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="team-add-dialog">
          <DialogHeader>
            <DialogTitle>Add a team member</DialogTitle>
          </DialogHeader>
          <form onSubmit={onAdd} className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-500">Full name</Label>
              <Input required value={form.name}
                     onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                     className="mt-1.5" data-testid="team-name-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Email</Label>
              <Input required type="email" value={form.email}
                     onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                     className="mt-1.5" data-testid="team-email-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Temporary password</Label>
              <Input required value={form.password}
                     onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                     className="mt-1.5 font-mono" data-testid="team-password-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Role</Label>
              <select
                value={form.role}
                onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
                className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
                data-testid="team-role-input"
              >
                <option value="tenant_member">Member (can create/edit)</option>
                <option value="tenant_admin">Admin (can manage team)</option>
              </select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy}
                      data-testid="team-submit-btn"
                      className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Add member
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
